import 'react-datepicker/dist/react-datepicker.css';

const colors = {
	timeBackground: 'white.900',
	border: 'gray.600',
	selectedDark: 'primary.800',
	green: 'green.500',

	background: {
		selected: 'border.accent.primary.default',
		inSelection: 'background.accent.primary.hovered',
		hoverSelected: 'background.accent.primary.hovered',
		hover: 'background.neutral.tertiary.hovered',
	},
	textColor: {
		default: 'text.neutral.primary.default',
		disabled: 'text.neutral.primary.disabled',
		selected: 'text.accent.onDark.default',
	},
};

const borderRadius = '30px';

const datepicker = {
	'.datepicker': {
		display: 'flex',
		alignItems: 'center',
	},
	'.react-datepicker': {
		border: 'none',
		backgroundColor: 'transparent',
	},
	'.react-datepicker__input-container': {
		input: {
			backgroundColor: 'transparent',
			border: 'none',
			cursor: 'pointer',
			fontSize: '16px',
			padding: 0,
			width: '100%',
		},
		'.react-datepicker__time-container--with-today-button': {
			borderRadius: '0.28125rem',
		},
	},
	'.react-datepicker__header': {
		bg: 'transparent',
		border: 'none',
		p: 0,
	},
	'.react-datepicker__day-names': {
		display: 'flex',
		height: '22px',
		m: 0,
		mb: 2,
	},
	'.react-datepicker__month-container': {
		width: '100%',
	},
	'.react-datepicker__month': {
		m: 0,
	},
	'.react-datepicker__week': {
		display: 'flex',
		m: 0,
		mt: 1,
	},
	'.react-datepicker__day, .react-datepicker__day-name': {
		flex: 1,
		m: 0,
	},
	'.react-datepicker__current-month, .react-datepicker-time__header, .react-datepicker-year-header':
		{
			fontSize: '0.944rem',
		},
	'.react-datepicker__day-name, .react-datepicker__day, .react-datepicker__time-name':
		{
			fontSize: '0.8rem',
			lineHeight: '1.875rem',
			width: '1.875rem',
			color: colors.textColor.default,
		},
	'@media (max-width: 400px), (max-height: 550px)': {
		'.react-datepicker__day-name, .react-datepicker__day, .react-datepicker__time-name':
			{
				fontSize: '0.8rem',
				lineHeight: '1.875rem',
				width: '1.875rem',
			},
	},
	'.react-datepicker__time-container': {
		float: 'right',
		borderLeft: '1px solid',
		borderLeftColor: colors.border,
		width: '85px',

		'&--with-today-button': {
			display: 'inline',
			border: '1px solid',
			borderColor: colors.border,
			borderRadius,
			position: 'absolute',
			right: '-72px',
			top: 0,
		},

		'.react-datepicker__time': {
			position: 'relative',
			background: colors.timeBackground,
			borderBottomRightRadius: borderRadius,

			'.react-datepicker__time-box': {
				width: '85px',
				overflowX: 'hidden',
				margin: '0 auto',
				textAlign: 'center',
				borderBottomRightRadius: borderRadius,

				'ul.react-datepicker__time-list': {
					listStyle: 'none',
					margin: 0,
					height: 'calc(195px + (#{$datepicker__item-size} / 2))',
					overflowY: 'scroll',
					paddingRight: 0,
					paddingLeft: 0,
					width: '100%',
					boxSizing: 'content-box',

					'li.react-datepicker__time-list-item': {
						height: '30px',
						padding: '5px 10px',
						whiteSpace: 'nowrap',

						'&:hover': {
							cursor: 'pointer',
							backgroundColor: colors.background.hover,
						},

						'&--selected': {
							backgroundColor: colors.background.selected,
							color: colors.textColor.selected,
							fontWeight: 'bold',

							'&:hover': {
								backgroundColor: colors.background.selected,
							},
						},

						'&--disabled': {
							color: 'text.neutral.secondary.default',

							'&:hover': {
								cursor: 'default',
								backgroundColor: 'transparent',
							},
						},
					},
				},
			},
		},
	},
	'.react-datepicker__day, .react-datepicker__month-text, .react-datepicker__quarter-text, .react-datepicker__year-text':
		{
			cursor: 'pointer',

			'&:hover': {
				borderRadius,
				backgroundColor: colors.background.hover,
			},

			'&--today': {
				fontWeight: 'bold',
			},

			'&--highlighted': {
				borderRadius,
				backgroundColor: colors.green,
				color: colors.textColor.selected,

				'&:hover': {
					backgroundColor: colors.selectedDark,
				},

				'&-custom-1': {
					color: 'magenta',
				},

				'&-custom-2': {
					color: colors.green,
				},
			},

			'&--selected,  &--in-selecting-range, &--in-range': {
				color: colors.textColor.selected,
				position: 'relative',
				borderRadius: 0,

				'&:hover': {
					backgroundColor: colors.background.hoverSelected,
					borderRadius: 0,
				},
			},

			'&:has(div.within-selecting-range, div.within-selected-range)': {
				borderRadius: 0,
				color: colors.textColor.selected,
				position: 'relative',

				// Show after and before elements to correctly blend selected dates and in selection dates
				'&:after, &:before': {
					content: '""',
					position: 'absolute',
					display: 'block',
					top: 0,
					width: '50%',
					height: '100%',
					pointerEvents: 'none',
				},

				'&:before': {
					left: '-50%',
					zIndex: -1,
				},

				'&:after': {
					right: '-50%',
					zIndex: -1,
				},

				'&:last-child:after': {
					display: 'none',
				},

				'&:first-of-type:before': {
					display: 'none',
				},
			},

			'&:has(div.within-selecting-range)': {
				backgroundColor: colors.background.inSelection,

				'&:before, &:after': {
					background: colors.background.inSelection,
				},

				'&:hover': {
					backgroundColor: colors.background.inSelection,

					// Blend selected dates and in selection dates for start and end dates when hovering
					'&:has(div.start-date, div.end-date)': {
						'&:has(div.within-selected-range):not(:has(div.within-offset-range))':
							{
								backgroundColor: colors.background.selected,

								'&:not(:has(div.selected-start-date, div.selected-end-date))': {
									borderRadius: 0,
								},

								'&:after': {
									display: 'none',
								},

								'& div': {
									backgroundColor: colors.background.inSelection,
									borderRadius,
									zIndex: 10,
								},
							},
					},
				},

				// Blend selected dates and in selection dates for start and end dates for offset picker
				'&:has(div.start-date, div.end-date)': {
					'&:has(div.within-selected-range):has(div.within-offset-range)': {
						backgroundColor: colors.background.selected,
						borderRadius: 0,

						'& div': {
							borderRadius,
							backgroundColor: colors.background.inSelection,
						},

						'&:has(div.start-date):not(:has(div.end-date))': {
							'& div': {
								borderTopRightRadius: 0,
								borderBottomRightRadius: 0,
							},
						},

						'&:has(div.end-date):not(:has(div.start-date))': {
							'& div': {
								borderTopLeftRadius: 0,
								borderBottomLeftRadius: 0,
							},
						},

						'&:has(div.selected-start-date)': {
							borderTopLeftRadius: borderRadius,
							borderBottomLeftRadius: borderRadius,
						},

						'&:has(div.selected-end-date)': {
							borderTopRightRadius: borderRadius,
							borderBottomRightRadius: borderRadius,
						},
					},
				},
			},

			'&:has(div.within-selected-range)': {
				backgroundColor: colors.background.selected,
				fontWeight: 'bold',

				'&:before, &:after': {
					background: colors.background.selected,
				},
			},

			// Rounded left corners for start date
			'&:has(div.start-date):not(:has(div.within-selected-range)), :has(div.selected-start-date)':
				{
					borderTopLeftRadius: borderRadius,
					borderBottomLeftRadius: borderRadius,

					'&:before': {
						display: 'none !important',
					},

					'&:hover': {
						borderTopLeftRadius: borderRadius,
						borderBottomLeftRadius: borderRadius,
					},
				},

			// Rounded right corners for end date
			'&:has(div.end-date):not(:has(div.within-selected-range)), :has(div.selected-end-date)':
				{
					borderTopRightRadius: borderRadius,
					borderBottomRightRadius: borderRadius,

					'&:after': {
						display: 'none !important',
					},

					'&:hover': {
						borderTopRightRadius: borderRadius,
						borderBottomRightRadius: borderRadius,
					},
				},

			'&:has(div.within-offset-range)': {
				backgroundColor: colors.background.inSelection,
			},

			'.react-datepicker__month--selecting-range &--in-range:not(&--in-selecting-range)':
				{
					backgroundColor: colors.background.selected,
					color: colors.textColor.selected,
				},

			'&--keyboard-selected': {
				borderRadius: 0,
				backgroundColor: colors.background.hoverSelected,
				color: colors.textColor.selected,

				'&:hover': {
					backgroundColor: colors.background.hoverSelected,
				},
			},

			'&--selected:not(&--in-selecting-range):not(&--in-range), &--keyboard-selected:not(&--in-selecting-range):not(&--in-range)':
				{
					borderRadius,
					backgroundColor: colors.background.selected,
				},

			'&--in-selecting-range:not(&--in-range)': {
				backgroundColor: colors.background.inSelection,
			},

			'&--disabled': {
				cursor: 'default',
				color: colors.textColor.disabled,

				'&:hover': {
					backgroundColor: 'transparent',
				},
			},
		},
};

export default datepicker;
